import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { HttpService } from '@/features/api/http.service';
import type {
  ICreateLocationDealRequest,
  IHubSpotCompany,
  IHubSpotContact,
  IHubSpotOwner,
  IHubSpotOwnerList,
  INearbyPlaceList,
  IZoneCandidate,
} from '@/types/api';

const HUBSPOT_PATH = '/integrations/hubspot';
const DEAL_CREATION_FACTORY_NAME = 'dealCreation';

/**
 * Read-side keys for the creation form's lookups. The run's own progress is NOT a query — it
 * streams over SSE via `useDealCreation`.
 */
export const dealCreationKeyFactory = createQueryKeys(DEAL_CREATION_FACTORY_NAME, {
  companies: (name: string) => ({
    queryKey: [{ name }],
    queryFn: () =>
      HttpService.get<string, { companies: IHubSpotCompany[] }>(
        `${HUBSPOT_PATH}/companies?name=${encodeURIComponent(name)}`,
      ),
  }),
  contacts: (query: string) => ({
    queryKey: [{ query }],
    queryFn: () =>
      HttpService.get<string, { contacts: IHubSpotContact[] }>(
        `${HUBSPOT_PATH}/contacts/search?query=${encodeURIComponent(query)}`,
      ),
  }),
  owner: (email: string) => ({
    queryKey: [{ email }],
    queryFn: () =>
      HttpService.get<string, IHubSpotOwner>(
        `${HUBSPOT_PATH}/owners?email=${encodeURIComponent(email)}`,
      ),
  }),
  assignableOwners: () => ({
    queryKey: ['assignable'],
    queryFn: () => HttpService.get<string, IHubSpotOwnerList>(`${HUBSPOT_PATH}/owners/assignable`),
  }),
  zoneCandidates: (locationId: string) => ({
    queryKey: [{ locationId }],
    queryFn: () =>
      HttpService.get<string, { candidates: IZoneCandidate[] }>(`/locations/${locationId}/zones`),
  }),
  nearbyPlaces: (point: GeoPoint) => ({
    queryKey: [point.latitude, point.longitude],
    queryFn: () =>
      HttpService.get<undefined, INearbyPlaceList>('/places/nearby', {
        latitude: point.latitude,
        longitude: point.longitude,
      }),
  }),
});

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/**
 * Search companies by name. Only ever fired on an explicit search, never as the user types: the
 * portal's search index is rate-limited, and an empty result is not proof the company does not
 * exist.
 */
export function useHubSpotCompanies(name: string, enabled: boolean) {
  return useQuery({ ...dealCreationKeyFactory.companies(name), enabled: enabled && name !== '' });
}

/**
 * Search contacts by name or email. Fired on an explicit search only, never as the BD types: the
 * portal's search index is rate-limited, and an empty result is not proof the contact does not
 * exist.
 */
export function useHubSpotContacts(query: string, enabled: boolean) {
  return useQuery({ ...dealCreationKeyFactory.contacts(query), enabled: enabled && query !== '' });
}

/**
 * Resolve the deal owner's email to a CRM user. A miss warns without blocking: an unowned deal is
 * recoverable, and the acting user is never substituted for it.
 */
export function useHubSpotOwner(email: string, enabled: boolean) {
  return useQuery({ ...dealCreationKeyFactory.owner(email), enabled: enabled && email !== '' });
}

/** The CRM users a deal can be assigned to. Fired once the BD opens the list, not on mount. */
export function useAssignableHubSpotOwners(enabled: boolean) {
  return useQuery({ ...dealCreationKeyFactory.assignableOwners(), enabled });
}

/** The hunting zones the location's point resolved to, closest first. */
export function useLocationZoneCandidates(locationId: string, enabled: boolean) {
  return useQuery({
    ...dealCreationKeyFactory.zoneCandidates(locationId),
    enabled: enabled && locationId !== '',
  });
}

/**
 * The published places around the location, most prominent first, offered as the deal's point of
 * interest. A failure leaves the field a plain text input: the list is a shortcut, not a gate.
 */
export function useNearbyPlaces(point: GeoPoint | undefined) {
  return useQuery({
    ...dealCreationKeyFactory.nearbyPlaces(point ?? { latitude: 0, longitude: 0 }),
    enabled: point !== undefined,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Start the creation. It answers 202 with no body: the run's outcome arrives over the stream
 * `useDealCreation` opens, not from this call.
 */
export function useStartDealCreation(locationId: string) {
  return useMutation({
    mutationFn: (body: ICreateLocationDealRequest): Promise<void> =>
      HttpService.post<ICreateLocationDealRequest, void>(`/locations/${locationId}/deal`, body),
  });
}
